import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { copyElement, deleteElement, listElements } from '../../../api/elements.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function ApplicationElementsListPage() {
  const { applicationId } = useOutletContext()
  const { confirm, toast } = useFeedback()
  const navigate = useNavigate()
  const [state, setState] = useState({
    status: 'loading',
    data: [],
    groupConfigs: [],
    error: null,
  })
  const [filterGroupConfigId, setFilterGroupConfigId] = useState('')

  useEffect(() => {
    let cancelled = false

    setState({
      status: 'loading',
      data: [],
      groupConfigs: [],
      error: null,
    })

    Promise.all([listElements(applicationId), listGroupConfigs(applicationId)])
      .then(([data, groupConfigs]) => {
        if (cancelled) return
        const sorted = [...data].sort((a, b) => {
          const nameA = String(a.name || '')
          const nameB = String(b.name || '')
          const gcA = String(a.groupConfigId ?? '')
          const gcB = String(b.groupConfigId ?? '')
          if (gcA !== gcB) return gcA.localeCompare(gcB)
          return nameA.localeCompare(nameB)
        })
        setState({
          status: 'ok',
          data: sorted,
          groupConfigs: groupConfigs || [],
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          data: [],
          groupConfigs: [],
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  const groupConfigsById = useMemo(() => {
    const map = {}
    for (const gc of state.groupConfigs) {
      map[String(gc.id)] = gc
    }
    return map
  }, [state.groupConfigs])

  const filteredData = useMemo(() => {
    if (!filterGroupConfigId) return state.data
    return state.data.filter((el) => String(el.groupConfigId || '') === String(filterGroupConfigId))
  }, [state.data, filterGroupConfigId])

  async function handleDelete(elementId) {
    const confirmed = await confirm({
      title: 'Delete element',
      message: 'Delete this element? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteElement(applicationId, elementId)
      const nextData = state.data.filter((item) => String(item.id) !== String(elementId))
      setState((prev) => ({
        ...prev,
        status: 'ok',
        data: nextData,
        error: null,
      }))
    } catch (e) {
      toast({
        type: 'error',
        message: String(e?.message || e),
      })
    }
  }

  async function handleCopy(elementId) {
    try {
      const created = await copyElement(applicationId, elementId)
      const [nextData, groupConfigs] = await Promise.all([
        listElements(applicationId),
        listGroupConfigs(applicationId),
      ])
      nextData.sort((a, b) => {
        const nameA = String(a.name || '')
        const nameB = String(b.name || '')
        const gcA = String(a.groupConfigId ?? '')
        const gcB = String(b.groupConfigId ?? '')
        if (gcA !== gcB) return gcA.localeCompare(gcB)
        return nameA.localeCompare(nameB)
      })
      setState((prev) => ({
        status: 'ok',
        data: nextData,
        groupConfigs: groupConfigs || prev.groupConfigs,
        error: null,
      }))
      navigate(`/applications/${applicationId}/elements/${created.id}`)
    } catch (e) {
      toast({
        type: 'error',
        message: String(e?.message || e),
      })
    }
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">Elements</div>
        </div>
        <div className="buttonGroup">
          {state.status === 'ok' && state.groupConfigs.length > 0 ? (
            <select
              className="textInput selectFilter"
              value={filterGroupConfigId}
              onChange={(e) => setFilterGroupConfigId(e.target.value)}
              aria-label="Filter by group config"
            >
              <option value="">All group configs</option>
              {state.groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          ) : null}
          <Link to={`/applications/${applicationId}/elements/new`} className="button buttonPrimary">
            Create
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <p className="muted inlineMessage">
          Loading elements...
        </p>
      ) : null}

      {state.status === 'error' ? (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      ) : null}

      {state.status === 'ok' && state.data.length === 0 ? (
        <p className="muted inlineMessage">
          No elements yet.
        </p>
      ) : null}

      {state.status === 'ok' && filteredData.length === 0 && state.data.length > 0 ? (
        <p className="muted inlineMessage">
          No elements match the selected filter.
        </p>
      ) : null}

      {state.status === 'ok' && filteredData.length > 0 ? (
        <table className="table" aria-label="Elements list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Group config</th>
              <th>Description</th>
              <th>Version</th>
              <th>Published</th>
              <th>Created</th>
              <th className="alignRight" />
            </tr>
          </thead>
          <tbody>
            {filteredData.map((el) => {
              const gc = el.groupConfigId != null ? groupConfigsById[String(el.groupConfigId)] : null
              return (
                <tr key={el.id}>
                  <td>{el.name || 'Unnamed'}</td>
                  <td className="muted">
                    {gc ? (gc.name || `#${el.groupConfigId}`) : ''}
                  </td>
                  <td className="muted">{el.description ?? ''}</td>
                  <td className="muted">{el.version ?? ''}</td>
                  <td>
                    {el.status === 'published' ? (
                      <span className="statusIcon statusIconSuccess" role="img" aria-label="Published">✓</span>
                    ) : (
                      <span className="statusIcon statusIconError" role="img" aria-label="Not published">✗</span>
                    )}
                  </td>
                  <td>{formatDate(el.createdAt)}</td>
                  <td className="alignRight">
                    <span className="tableActions">
                      <Link
                        to={`/applications/${applicationId}/elements/${el.id}`}
                        className="button buttonGhost"
                      >
                        View
                      </Link>
                      <button type="button" className="button buttonGhost" onClick={() => handleCopy(el.id)}>
                        Copy
                      </button>
                      <button
                        type="button"
                        className="button buttonGhost buttonDanger"
                        onClick={() => handleDelete(el.id)}
                        disabled
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  )
}

